import { useMemo } from "react";

interface FilterOptions<T> {
  searchString?: string;
  searchFields?: (keyof T)[];
  appliedFilters?: { [key: string]: string[] };
  customFilters?: {
    [key: string]: (
      items: T[],
      selectedValues: string[],
      additionalData?: any,
    ) => T[];
  };
  additionalData?: any;
}

const applySearch = <T>(
  items: T[],
  searchString: string,
  searchFields: (keyof T)[],
): T[] => {
  if (!searchString || !searchFields.length) return items;

  const searchLower = searchString.toLowerCase();
  return items.filter((item) =>
    searchFields.some((field) => {
      const value = item[field];
      return (
        typeof value === "string" && value.toLowerCase().includes(searchLower)
      );
    }),
  );
};

const applyFilters = <T>(
  items: T[],
  appliedFilters: { [key: string]: string[] },
  customFilters: {
    [key: string]: (
      items: T[],
      selectedValues: string[],
      additionalData?: any,
    ) => T[];
  },
  additionalData?: any,
): T[] => {
  let filteredItems = items;

  Object.entries(appliedFilters).forEach(([filterKey, selectedValues]) => {
    if (selectedValues.length > 0) {
      const customFilter = customFilters[filterKey];
      if (customFilter) {
        filteredItems = customFilter(
          filteredItems,
          selectedValues,
          additionalData,
        );
      } else {
        // Default filter: check if item[filterKey] is in selectedValues
        filteredItems = filteredItems.filter((item) => {
          const value = (item as any)[filterKey];
          return selectedValues.includes(String(value));
        });
      }
    }
  });

  return filteredItems;
};

export const useTableFiltering = <T>(
  items: T[],
  filterOptions: FilterOptions<T>,
) => {
  return useMemo(() => {
    const {
      searchString = "",
      searchFields = [],
      appliedFilters = {},
      customFilters = {},
      additionalData,
    } = filterOptions;

    let filteredItems = Array.isArray(items) ? items : [];

    // Apply search filter
    if (searchString && searchFields.length > 0) {
      filteredItems = applySearch(filteredItems, searchString, searchFields);
    }

    // Apply dropdown filters
    if (Object.keys(appliedFilters).length > 0) {
      filteredItems = applyFilters(
        filteredItems,
        appliedFilters,
        customFilters,
        additionalData,
      );
    }

    return filteredItems;
  }, [items, filterOptions]);
};
